/**
 * QL-375 group 5 / QL-664 — the mandate card's controller: the options it
 * offers, the local pick and note, and what Save does with each.
 *
 * Two variants of one card:
 *   - "deviation" — on a breach. Update mandate based on this portfolio
 *     (portfolio page) or on the goal's aggregated 1-year portfolio (goal
 *     page), then Accept with a note, then Block.
 *   - "missing"   — a required goal with nothing resolved. Create mandate from
 *     this portfolio (portfolio page), then Block where the gate sits.
 *
 * The update / create options are actions through the Update endpoints —
 * never a stored decision, since the breach (or the gap) is gone afterwards.
 * Accept and Block are the stored decision, with "Accepted by {name} · {time
 * ago}" and the note beneath. Shown read-only on a locked instance.
 */

import type {
  MandateDecision,
  Translated,
  UpdateFromGoalAvailability,
  UpdateFromGoalDisabledReason,
  UpdateFromPortfolioAvailability,
  UpdateFromPortfolioDisabledReason,
} from "@repo/shared/types";
import { formatDistanceToNow } from "date-fns";
import { enGB, nb } from "date-fns/locale";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/api/_client";
import {
  useMandateDecision,
  useSetMandateBasisPortfolio,
  useUpdateMandateFromGoal,
} from "@/lib/hooks/use-flow-instances/use-mandate";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./mandate-decision-section.i18n";
import {
  type MandateCardOption,
  type MandateCardOptionId,
  MandateDeviationCard,
} from "./mandate-deviation-card";

/** QL-664 — the goal page's Update option, disabled with the server's one reason. */
const updateFromGoalUnavailableText = (
  reason: UpdateFromGoalDisabledReason | null,
  t: Translated<typeof T>,
): string | null => {
  // QL-695 — `setting_off` hides the option instead of disabling it (no text).
  switch (reason) {
    case "no_basis":
      return t.updateFromGoalUnavailableNoBasis;
    case "no_isin":
      return t.updateFromGoalUnavailableNoIsin;
    case "no_weights":
      return t.updateFromGoalUnavailableNoWeights;
    default:
      return null;
  }
};

/** Screen 07's footer sentence for an unavailable Update, from the server's one reason. */
const updateUnavailableText = (
  reason: UpdateFromPortfolioDisabledReason | null,
  t: Translated<typeof T>,
): string | null => {
  // QL-695 — `setting_off` hides the option instead of disabling it (no text).
  switch (reason) {
    case "no_products":
      return t.updateUnavailableNoProducts;
    case "not_custom":
      return t.updateUnavailableNotCustom;
    case "no_weights":
      return t.updateUnavailableNoWeights;
    case "no_isin":
      return t.updateUnavailableNoIsin;
    default:
      return null;
  }
};

type Props = {
  variant: "deviation" | "missing";
  instanceId: string;
  goalId: string;
  /** Group 7 — under portfolio-level blocking the decision is the portfolio's own. */
  portfolioId?: string | null;
  stored: MandateDecision | null;
  /**
   * Portfolio page: whether this portfolio may write the goal's mandate (the
   * server's answer); the option is offered disabled with the reason otherwise.
   * Null on the goal page.
   */
  updateFromPortfolio?: UpdateFromPortfolioAvailability | null;
  /** QL-695 — the mandate is the bank's: the create option's hint says the proposal replaces it. */
  replacesExisting?: boolean;
  /** Goal page: whether the goal's combined allocation may be written. Null on the portfolio page. */
  updateFromGoal?: UpdateFromGoalAvailability | null;
  /**
   * Whether Block is offered: where the gate sits (the portfolio page under
   * portfolio-level blocking, the goal page under goal-level). Accept follows
   * the same rule; the update / create actions are offered regardless.
   */
  decisionsHere: boolean;
  language: string;
  disabled: boolean;
  footer?: ReactNode;
};

export function MandateDecisionSection({
  variant,
  instanceId,
  goalId,
  portfolioId = null,
  stored,
  updateFromPortfolio = null,
  replacesExisting = false,
  updateFromGoal = null,
  decisionsHere,
  language,
  disabled,
  footer,
}: Props) {
  const t = useT(T);
  // Synced from the server, editable locally until Save.
  const [value, setValue] = useState<MandateCardOptionId | "">("");
  const [justification, setJustification] = useState("");
  // QL-677 — on the mandate's basis portfolio the create option reads as
  // selected from the server, whatever is stored.
  const isBasis = !!portfolioId && !!updateFromPortfolio?.isBasis;
  useEffect(() => {
    setValue(
      stored?.decision ??
        (isBasis
          ? variant === "missing"
            ? "create_from_portfolio"
            : "update_from_portfolio"
          : ""),
    );
    setJustification(stored?.justification ?? "");
  }, [stored?.decision, stored?.justification, isBasis, variant]);

  const decide = useMandateDecision(instanceId, goalId, portfolioId);
  const updateFromPf = useSetMandateBasisPortfolio(instanceId, goalId, portfolioId ?? "");
  const updateFromGl = useUpdateMandateFromGoal(instanceId, goalId);
  const isSaving = decide.isPending || updateFromPf.isPending || updateFromGl.isPending;

  const options: MandateCardOption[] = [];
  // QL-695 — the collection's "Allow creation of mandate from portfolio"
  // switch off HIDES the create options (an existing basis never reads
  // setting_off, so it stays); the other reasons disable with their hint.
  // QL-730 B5 — a selected template hides them the same way, on both pages:
  // the server's reason, so every surface agrees (P3-2).
  const hidden = (reason: string | null) =>
    reason === "setting_off" || reason === "template_selected";
  if (portfolioId && updateFromPortfolio && !hidden(updateFromPortfolio.disabledReason)) {
    const create = variant === "missing";
    const noProducts = updateFromPortfolio.disabledReason === "no_products";
    // QL-677 — one label in both variants ("Create mandate based on this
    // portfolio"); the hint says whether it creates or updates. Batch 5 makes
    // it the persistent basis; until then it is Part I's one-shot write.
    // QL-677 — persistent: on the basis portfolio the option is selected and
    // Save does nothing; a paused basis (an ISIN-less product) says so.
    options.push({
      id: create ? "create_from_portfolio" : "update_from_portfolio",
      label: t.optCreateFromPortfolio,
      hint: updateFromPortfolio.isBasis
        ? updateFromPortfolio.paused
          ? t.basisPausedHint(updateFromPortfolio.pausedProduct ?? "")
          : t.basisActiveHint
        : replacesExisting
          ? t.optCreateFromPortfolioHintExisting
          : create
            ? t.optCreateFromPortfolioHint
            : t.optCreateFromPortfolioHintCustom,
      disabled: !updateFromPortfolio.available && !updateFromPortfolio.isBasis,
      saved: updateFromPortfolio.isBasis,
      disabledHint:
        (create && noProducts ? t.createUnavailableNoProducts : null) ??
        updateUnavailableText(updateFromPortfolio.disabledReason, t) ??
        undefined,
    });
  }
  if (
    !portfolioId &&
    updateFromGoal &&
    variant === "deviation" &&
    !hidden(updateFromGoal.disabledReason)
  ) {
    options.push({
      id: "update_from_goal",
      label: t.optUpdateFromGoal,
      hint: t.optUpdateFromGoalHint,
      disabled: !updateFromGoal.available,
      disabledHint: updateFromGoalUnavailableText(updateFromGoal.disabledReason, t) ?? undefined,
    });
  }
  if (decisionsHere) {
    if (variant === "deviation") {
      options.push({ id: "accepted", label: t.accept, hint: t.acceptHintBox });
    }
    options.push({
      id: "do_not_proceed",
      label: t.block,
      hint: variant === "missing" ? t.blockHintMissing : t.blockHint,
    });
  }
  if (options.length === 0) return null;

  const saveDecision = async (decision: "accepted" | "do_not_proceed" | null) => {
    try {
      const result = await decide.mutateAsync({
        decision,
        ...(decision === "accepted" ? { justification } : {}),
      });
      if (!result.success) {
        toast.error(t.error, { description: result.error });
        return;
      }
      toast.success(decision === null ? t.decisionCleared : t.decisionSaved);
    } catch (err) {
      toast.error(t.error, { description: apiErrorMessage(err, t.error) });
    }
  };

  const writeMandate = async (source: "portfolio" | "goal", successText: string) => {
    try {
      const result =
        source === "portfolio"
          ? await updateFromPf.mutateAsync()
          : await updateFromGl.mutateAsync();
      if (!result.success) {
        toast.error(t.updateError, { description: result.error });
        return;
      }
      toast.success(successText);
    } catch (err) {
      toast.error(t.updateError, { description: apiErrorMessage(err, t.updateError) });
    }
  };

  const handleSave = (picked: MandateCardOptionId) => {
    switch (picked) {
      case "update_from_portfolio":
        return writeMandate("portfolio", t.updateApplied);
      case "create_from_portfolio":
        return writeMandate("portfolio", t.createApplied);
      case "update_from_goal":
        return writeMandate("goal", t.updateFromGoalApplied);
      case "accepted":
      case "do_not_proceed":
        return saveDecision(picked);
    }
  };

  const decidedAgo = stored?.decidedAt
    ? formatDistanceToNow(new Date(stored.decidedAt), {
        addSuffix: true,
        locale: language === "nb_NO" ? nb : enGB,
      })
    : "";

  return (
    <div className="space-y-2">
      <MandateDeviationCard
        idPrefix={`md-${portfolioId ?? goalId}`}
        title={variant === "missing" ? t.missingTitle : t.deviationTitle}
        subtitle={variant === "missing" ? t.missingSubtitle : t.deviationSubtitle}
        options={options}
        value={value}
        onValueChange={setValue}
        justification={justification}
        onJustificationChange={setJustification}
        noteLabel={t.noteLabel}
        notePlaceholder={t.notePlaceholder}
        saveLabel={t.save}
        clearLabel={t.clearDecision}
        hasStoredDecision={stored !== null}
        onSave={handleSave}
        onClear={() => saveDecision(null)}
        isSaving={isSaving}
        disabled={disabled}
        footer={footer}
      />
      {stored && (
        <div className="text-sm text-muted-foreground space-y-0.5">
          {stored.justification && (
            <p>
              {t.noteLabel}: {stored.justification}
            </p>
          )}
          <p className="text-xs">
            {(stored.decision === "accepted" ? t.acceptedBy : t.blockedBy)({
              name: stored.decidedBy?.name ?? "",
              time: decidedAgo,
            })}
          </p>
        </div>
      )}
    </div>
  );
}
